import { useState, useRef } from 'react';
import { BreakdownInfo } from '../../../Home/types';
import { SceneInfo, SceneWithUserNames, UpdateSceneParams } from '../../../../types/scene.types';
import { ElementType } from '../../../../types/element.types';
import { ScheduleInfo } from '../../../../types/schedule.types';
import { SceneRef } from '../../SceneDetails/SceneDetails';
import { SortConfig, MenuPosition } from '../BreakdownMain.types';

/**
 * 统一管理分场页 UI 状态（含本分场下场次行列表 scenes）
 */
export const useScenesState = () => {
  // ==================== 基础状态 ====================
  const [breakdownInfo, setBreakdownInfo] = useState<BreakdownInfo | null>(null);
  const [scenes, setScenes] = useState<SceneWithUserNames[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedSceneId, setSelectedSceneId] = useState<string | null>(null);
  const [selectedSceneData, setSelectedSceneData] = useState<Partial<SceneInfo> | undefined>(undefined);
  
  // 分场名称编辑相关状态
  const [breakdownName, setBreakdownName] = useState('未命名分场');
  const [originalBreakdownName, setOriginalBreakdownName] = useState<string>('');
  
  // 排序状态管理
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: 'sceneCode',
    direction: 'asc'
  });

  // ==================== SetSearch相关状态 ====================
  const [setSearchVisible, setSetSearchVisible] = useState(false);
  const [setSearchText, setSetSearchText] = useState('');
  const [activeSceneId, setActiveSceneId] = useState<string | null>(null); // 当前正在编辑场景的场次ID
  const [setSearchPosition, setSetSearchPosition] = useState<MenuPosition | null>(null); // SetSearch组件的位置和宽度
  
  // 输入法状态管理（用于区分拼音输入和汉字选择完成）
  const [isComposingSceneSet, setIsComposingSceneSet] = useState<Map<string, boolean>>(new Map());
  const [isComposingSceneLocation, setIsComposingSceneLocation] = useState<Map<string, boolean>>(new Map());
  
  // 本地显示值（用于在输入法输入期间显示拼音）
  const [localSceneSetDisplay, setLocalSceneSetDisplay] = useState<Map<string, string>>(new Map());
  const [localSceneLocationDisplay, setLocalSceneLocationDisplay] = useState<Map<string, string>>(new Map());
  
  // ==================== 地点搜索相关状态 ====================
  const [sceneLocationSearchVisible, setSceneLocationSearchVisible] = useState(false);
  const [sceneLocationSearchText, setSceneLocationSearchText] = useState('');
  const [activeLocationSceneId, setActiveLocationSceneId] = useState<string | null>(null); // 当前正在编辑地点的场次ID
  const [sceneLocationSearchPosition, setSceneLocationSearchPosition] = useState<MenuPosition | null>(null); // 地点搜索组件的位置和宽度
  const [sceneLocationDisplayTexts, setSceneLocationDisplayTexts] = useState<{ [sceneId: string]: string }>({}); // 缓存场景地点显示文本
  const [sceneSetDisplayTexts, setSceneSetDisplayTexts] = useState<{ [sceneId: string]: string }>({}); // 缓存场景字段显示文本
  const [sceneLocationPreviewText, setSceneLocationPreviewText] = useState<string | null>(null); // 地点输入预览文本
  const [setPreviewText, setSetPreviewText] = useState<string | null>(null); // 场景字段输入预览文本
  
  // ==================== 下拉菜单状态（内外、日夜）====================
  const [activeInteriorExteriorSceneId, setActiveInteriorExteriorSceneId] = useState<string | null>(null);
  const [activeDayNightSceneId, setActiveDayNightSceneId] = useState<string | null>(null);
  const [interiorExteriorMenuPosition, setInteriorExteriorMenuPosition] = useState<MenuPosition | null>(null);
  const [dayNightMenuPosition, setDayNightMenuPosition] = useState<MenuPosition | null>(null);
  
  // ==================== ElementSearch相关状态 ====================
  const [elementSearchVisible, setElementSearchVisible] = useState(false);
  const [elementSearchType, setElementSearchType] = useState<ElementType>(ElementType.CAST);
  
  // ==================== Scene组件刷新触发器 ====================
  const [sceneRefreshTrigger, setSceneRefreshTrigger] = useState(0);
  
  // ==================== 已选择的场次元素ID列表 ====================
  const [selectedElementIds, setSelectedElementIds] = useState<Set<string>>(new Set());

  // ==================== 本分场拍摄计划列表 ====================
  const [schedulesList, setSchedulesList] = useState<ScheduleInfo[]>([]);
  const [recentlyUpdatedScheduleId, setRecentlyUpdatedScheduleId] = useState<string | null>(null);

  // ==================== 每个分场最后选中的场次ID记录 ====================
  // 使用 ref 来存储，避免在状态更新时触发不必要的重渲染
  const lastSelectedSceneIdByBreakdownIdRef = useRef<Map<string, string | null>>(new Map());

  // ==================== Refs ====================
  // 用于收集待提交的场次变更（按场次维度合并字段，减少请求次数）
  const pendingSceneUpdatesRef = useRef<Map<string, Partial<UpdateSceneParams>>>(new Map());
  // 用于维护每个场次的防抖定时器
  const updateTimersRef = useRef<Map<string, number>>(new Map());
  // Scene组件的ref，用于调用其方法
  const sceneRef = useRef<SceneRef>(null);

  return {
    // 基础状态
    breakdownInfo,
    setBreakdownInfo,
    scenes,
    setScenes,
    loading,
    setLoading,
    error,
    setError,
    selectedSceneId,
    setSelectedSceneId,
    selectedSceneData,
    setSelectedSceneData,
    breakdownName,
    setBreakdownName,
    originalBreakdownName,
    setOriginalBreakdownName,
    sortConfig,
    setSortConfig,

    // SetSearch相关状态
    setSearchVisible,
    setSetSearchVisible,
    setSearchText,
    setSetSearchText,
    activeSceneId,
    setActiveSceneId,
    setSearchPosition,
    setSetSearchPosition,
    isComposingSceneSet,
    setIsComposingSceneSet,
    isComposingSceneLocation,
    setIsComposingSceneLocation,
    localSceneSetDisplay,
    setLocalSceneSetDisplay,
    localSceneLocationDisplay,
    setLocalSceneLocationDisplay,

    // 地点搜索相关状态
    sceneLocationSearchVisible,
    setSceneLocationSearchVisible,
    sceneLocationSearchText,
    setSceneLocationSearchText,
    activeLocationSceneId,
    setActiveLocationSceneId,
    sceneLocationSearchPosition,
    setSceneLocationSearchPosition,
    sceneLocationDisplayTexts,
    setSceneLocationDisplayTexts,
    sceneSetDisplayTexts,
    setSceneSetDisplayTexts,
    sceneLocationPreviewText,
    setSceneLocationPreviewText,
    setPreviewText,
    setSetPreviewText,

    // 下拉菜单状态
    activeInteriorExteriorSceneId,
    setActiveInteriorExteriorSceneId,
    activeDayNightSceneId,
    setActiveDayNightSceneId,
    interiorExteriorMenuPosition,
    setInteriorExteriorMenuPosition,
    dayNightMenuPosition,
    setDayNightMenuPosition,

    // ElementSearch相关状态
    elementSearchVisible,
    setElementSearchVisible,
    elementSearchType,
    setElementSearchType,

    // Scene组件刷新触发器
    sceneRefreshTrigger,
    setSceneRefreshTrigger,

    // 已选择的场次元素ID列表
    selectedElementIds,
    setSelectedElementIds,

    // 本分场拍摄计划列表（Schedule）
    schedulesList,
    setSchedulesList,
    recentlyUpdatedScheduleId,
    setRecentlyUpdatedScheduleId,

    // Refs
    pendingSceneUpdatesRef,
    updateTimersRef,
    sceneRef,
    lastSelectedSceneIdByBreakdownIdRef,
  };
};


